import { useCallback, useEffect, useRef } from 'react';
import { InternalEvent, type Graph } from '@maxgraph/core';

const RECENTER_DEBOUNCE_MS = 150;

/**
 * Keeps the graph centered in the canvas using maxGraph's own `center()` API —
 * no manual translate math and no scroll-position tracking. `center()` is
 * idempotent when the graph bounds don't change, so pure label/selection edits
 * cause no visible jump; it only re-centers when the graph grows or shrinks.
 *
 * Navigation is the (styled) container scrollbar. Drag-to-pan is intentionally
 * not wired here yet.
 */
export function useAutoCenter(graph: Graph | null) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const center = useCallback(() => {
    if (!graph) return;
    try {
      graph.center();
    } catch {
      // Graph may be mid-destruction or not yet ready.
    }
  }, [graph]);

  // Center on mount — the initial layout has already run synchronously by now.
  useEffect(() => {
    center();
  }, [center]);

  // Re-center after model changes (inserts / deletes / group / collapse),
  // debounced so a multi-step edit only centers once when it settles.
  useEffect(() => {
    if (!graph) return;

    const listener = () => {
      if (debounceTimerRef.current != null) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        center();
      }, RECENTER_DEBOUNCE_MS);
    };

    graph.getDataModel().addListener(InternalEvent.CHANGE, listener);
    return () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      graph.getDataModel().removeListener(listener);
    };
  }, [graph, center]);

  return { forceRecenter: center };
}
